import { toast } from 'react-hot-toast';

/**
 * Access Control Service
 * 
 * Provides frontend access control error handling and user feedback.
 * Manages access control errors with intelligent deduplication and
 * user-friendly error messages.
 * 
 * Features:
 * - Access control error detection
 * - Message deduplication
 * - User-friendly error display
 * - Intelligent error routing
 * - Toast notification integration
 * 
 * @example
 * ```typescript
 * import { handleAccessControlError, isAccessControlError } from '@/services/accessControlService';
 * 
 * if (isAccessControlError(error)) {
 *   handleAccessControlError(error);
 * }
 * ```
 */

/**
 * Access Control Error interface
 * 
 * Defines the structure of access control errors returned by the backend
 * for consistent error handling and user feedback.
 */
export interface AccessControlError {
  /** Error code for programmatic handling */
  code: string;
  /** Human-readable error message */
  message: string;
  /** Additional error details for debugging */
  details?: {
    /** Module that was accessed */
    module?: string;
    /** Permission that was required */
    permission?: string;
    /** User role that attempted access */
    userRole?: string;
  };
}

/**
 * Message deduplication system
 * 
 * Prevents duplicate error messages from being displayed to users
 * by tracking active messages and their timeouts.
 */
class MessageDeduplicator {
  /** Set of currently active error messages */
  private activeMessages: Set<string> = new Set();
  /** Map of message timeouts for cleanup */
  private messageTimeouts: Map<string, NodeJS.Timeout> = new Map();

  isMessageActive(message: string): boolean {
    return this.activeMessages.has(message);
  }

  addMessage(message: string, timeoutMs: number = 5000): void {
    // Remove existing timeout if message is already active
    if (this.messageTimeouts.has(message)) {
      clearTimeout(this.messageTimeouts.get(message)!);
    }

    this.activeMessages.add(message);
    
    // Set timeout to remove message from active set
    const timeout = setTimeout(() => {
      this.activeMessages.delete(message);
      this.messageTimeouts.delete(message);
    }, timeoutMs);
    
    this.messageTimeouts.set(message, timeout);
  }

  clearAll(): void {
    this.activeMessages.clear();
    this.messageTimeouts.forEach(timeout => clearTimeout(timeout));
    this.messageTimeouts.clear();
  }
}

/**
 * Global deduplicator instance
 * 
 * Shared instance for managing message deduplication across the application.
 */
const messageDeduplicator = new MessageDeduplicator();

const GENERIC_MESSAGES = new Set([
  'Validation failed',
  'Operation failed',
  'Operation failed. Please try again.',
  'Failed to create contact',
  'Failed to create company',
  'Failed to create interaction',
  'An unexpected error occurred'
]);

const prettifyFieldName = (field: string): string =>
  field
    .replace(/\[(\d+)\]/g, ' $1 ')
    .replace(/[._]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());

const buildValidationMessage = (errors: any[]): string | null => {
  if (!Array.isArray(errors) || errors.length === 0) {
    return null;
  }

  const details = errors
    .slice(0, 3)
    .map((e: any) => {
      const field = typeof e?.path === 'string' && e.path.length > 0 ? `${prettifyFieldName(e.path)}: ` : '';
      const message = typeof e?.msg === 'string' && e.msg.length > 0 ? e.msg : 'Invalid value';
      return `${field}${message}`;
    })
    .join('; ');

  const remainingCount = Math.max(0, errors.length - 3);
  return remainingCount > 0
    ? `Please fix the following: ${details}; and ${remainingCount} more issue(s).`
    : `Please fix the following: ${details}.`;
};

const extractApiErrorMessage = (error: any, fallback: string): string => {
  const validationMessage = buildValidationMessage(error?.response?.data?.errors);
  const rawMessage =
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.message ||
    fallback;

  if (validationMessage) {
    return validationMessage;
  }

  if (typeof rawMessage === 'string' && GENERIC_MESSAGES.has(rawMessage.trim())) {
    const backendDetails =
      error?.response?.data?.error?.details ||
      error?.response?.data?.details ||
      error?.response?.data?.error;
    if (typeof backendDetails === 'string' && backendDetails.trim()) {
      return backendDetails;
    }
  }

  return rawMessage;
};

/**
 * Handle access control errors gracefully with user-friendly messages
 * 
 * Processes access control errors and displays appropriate user feedback
 * while preventing duplicate messages through deduplication.
 * 
 * NOTE: Access control errors should be rare - the UI should only show
 * items users have access to. If an error occurs, it means there's a
 * mismatch between navigation permissions and page/API permissions.
 * 
 * @param error - Error object from API response
 * 
 * @example
 * ```typescript
 * try {
 *   await api.get('/protected-resource');
 * } catch (error) {
 *   handleAccessControlError(error);
 * }
 * ```
 */
export const handleAccessControlError = (error: any): void => {
  // Extract error information
  const errorData = error.response?.data?.error || error;
  const errorMessage = errorData.message || 'Access denied';

  // Log the error for debugging
  console.warn('[Access Control] Permission mismatch detected:', errorMessage);
  console.warn('[Access Control] This suggests navigation permissions and page permissions are out of sync.');
  
  // Don't show error messages - if navigation says user has access,
  // the page should also allow access. If there's a mismatch, it's a bug.
  // The UI should only show items users can access, so errors shouldn't happen.
};

/**
 * Check if an error is an access control error
 * 
 * Determines whether an error is related to access control by checking
 * the error code against known access control error codes.
 * 
 * @param error - Error object to check
 * @returns boolean - True if the error is an access control error
 * 
 * @example
 * ```typescript
 * if (isAccessControlError(error)) {
 *   // Handle access control error specifically
 * }
 * ```
 */
export const isAccessControlError = (error: any): boolean => {
  const errorCode = error.response?.data?.error?.code || error.code;
  const accessControlCodes = [
    'MODULE_ACCESS_DENIED',
    'PERMISSION_DENIED',
    'PLATFORM_ADMIN_REQUIRED',
    'TENANT_ADMIN_REQUIRED',
    'RESOURCE_ACCESS_DENIED',
    'DATA_ACCESS_DENIED',
    'ACCESS_DENIED'
  ];
  
  return accessControlCodes.includes(errorCode) || error.response?.status === 403;
};

/**
 * Enhanced API error handler that distinguishes between access control and other errors
 */
export const handleApiError = (error: any): void => {
  if (isAccessControlError(error)) {
    handleAccessControlError(error);
  } else {
    // Handle other types of errors (network, server, etc.)
    const message = extractApiErrorMessage(error, 'An unexpected error occurred');
    
    // Check for duplicate messages for non-access control errors too
    if (messageDeduplicator.isMessageActive(message)) {
            return;
    }

    messageDeduplicator.addMessage(message, 4000);
    
    toast.error(message, {
      duration: 4000,
      position: 'top-center'
    });
  }
};

/**
 * Handle access control errors in components
 * 
 * For access control errors, silently handle them (don't show error messages).
 * For other errors, show appropriate error messages.
 */
export const handleAccessControlErrorInComponent = (error: any): void => {
  if (isAccessControlError(error)) {
    // Silently handle access control errors - don't tell users what they can't access
    handleAccessControlError(error);
  } else {
    // For non-access-control errors, show error messages
    const fallbackMessage = 'Operation failed. Please try again.';
    const message = extractApiErrorMessage(error, fallbackMessage);
    
    // Check for duplicate messages
    if (messageDeduplicator.isMessageActive(message)) {
            return;
    }

    messageDeduplicator.addMessage(message, 4000);
    
    toast.error(message, {
      duration: 4000,
      position: 'top-center'
    });
  }
};

/**
 * Enhanced fetch wrapper with access control error handling
 */
export const fetchWithAccessControl = async (
  url: string, 
  options: RequestInit = {}
): Promise<Response> => {
  try {
    const response = await fetch(url, options);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = {
        response: {
          status: response.status,
          data: errorData
        }
      };
      
      if (isAccessControlError(error)) {
        handleAccessControlError(error);
      }
    }
    
    return response;
  } catch (error: any) {
    if (isAccessControlError(error)) {
      handleAccessControlError(error);
    }
    throw error;
  }
};

/**
 * Get user-friendly error message for specific error codes
 */
export const getAccessControlErrorMessage = (errorCode: string): string => {
  const messages: Record<string, string> = {
    'MODULE_ACCESS_DENIED': 'You don\'t have access to this section. Please contact your administrator if you need access.',
    'PERMISSION_DENIED': 'You don\'t have permission to perform this action. Please contact your administrator if you need this permission.',
    'PLATFORM_ADMIN_REQUIRED': 'This action requires platform administrator privileges. Please contact your system administrator.',
    'TENANT_ADMIN_REQUIRED': 'This action requires tenant administrator privileges. Please contact your organization administrator.',
    'RESOURCE_ACCESS_DENIED': 'You don\'t have access to this resource. Please contact your administrator if you believe this is an error.',
    'DATA_ACCESS_DENIED': 'You don\'t have permission to access this data. Please contact your administrator if you need access.',
    'ACCESS_DENIED': 'You don\'t have permission to perform this action. Please contact your administrator if you need access.'
  };

  return messages[errorCode] || messages['ACCESS_DENIED'];
};

/**
 * Clear all active messages (useful for testing or cleanup)
 */
export const clearActiveMessages = (): void => {
  messageDeduplicator.clearAll();
};
